import { useEffect, useState } from "react";
import { endpoints, fmtUSD, fmtTime } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATES = ["draft", "released", "authorized", "consumed", "expired", "deactivated"];

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => endpoints.offers().then(setOffers);
  useEffect(() => {
    load();
  }, []);

  const transition = async (id, state) => {
    await endpoints.transitionOffer(id, state);
    toast.success(`Offer moved to ${state}`);
    load();
  };

  const filtered = filter === "all" ? offers : offers.filter((o) => o.state === filter);
  const byState = STATES.map((s) => ({ s, n: offers.filter((o) => o.state === s).length }));

  return (
    <div className="px-12 py-14" data-testid="page-offers">
      <div className="tx-overline mb-2">Modules M4 · M6</div>
      <h1 className="font-heading font-black text-4xl mb-2">Private Offers</h1>
      <p className="text-zinc-600 text-[14px] max-w-2xl mb-8">
        Every CPPO Private Offer Autopilot has drafted, released or is tracking through consumption.
      </p>

      <div className="flex flex-wrap gap-0 border border-zinc-200 mb-6">
        <button
          onClick={() => setFilter("all")}
          data-testid="filter-all"
          className={`px-4 h-10 text-[12px] tx-overline border-r border-zinc-200 ${
            filter === "all" ? "bg-zinc-50 text-zinc-900" : "text-zinc-600 hover:bg-zinc-100"
          }`}
        >
          All · {offers.length}
        </button>
        {byState.map(({ s, n }) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            className={`px-4 h-10 text-[12px] tx-overline border-r border-zinc-200 last:border-r-0 ${
              filter === s ? "bg-zinc-50 text-zinc-900" : "text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            {s} · {n}
          </button>
        ))}
      </div>

      <div className="border border-zinc-200 bg-zinc-50">
        <table className="w-full text-[13px]">
          <thead className="border-b border-zinc-200">
            <tr>
              {["CPPO ID", "Buyer AWS", "Reseller", "Mode", "TCV", "Term", "State", "Released", "Actions"].map(
                (h) => (
                  <th key={h} className="tx-overline px-4 py-3 font-normal text-left">
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-zinc-500 text-[13px]">
                  No offers in this state.
                </td>
              </tr>
            )}
            {filtered.map((o) => (
              <tr key={o.id} className="border-b border-zinc-200 last:border-b-0 hover:bg-zinc-100">
                <td className="px-4 py-3 font-mono text-[11px] text-zinc-600">{o.id}</td>
                <td className="px-4 py-3 font-mono">{o.buyer_aws_account_id}</td>
                <td className="px-4 py-3">{o.reseller}</td>
                <td className="px-4 py-3 tx-overline">{o.mode}</td>
                <td className="px-4 py-3 font-mono">{fmtUSD(o.total_contract_value_usd)}</td>
                <td className="px-4 py-3">{o.duration_months}mo</td>
                <td className="px-4 py-3">
                  <StateBadge state={o.state} />
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-zinc-500">
                  {fmtTime(o.released_at)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {o.state === "draft" && (
                      <Button
                        size="sm"
                        onClick={() => transition(o.id, "released")}
                        className="h-7 px-2 text-[11px] bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm"
                      >
                        Release
                      </Button>
                    )}
                    {o.state === "released" && (
                      <Button
                        size="sm"
                        onClick={() => transition(o.id, "authorized")}
                        className="h-7 px-2 text-[11px] bg-zinc-200 hover:bg-zinc-300 rounded-sm"
                      >
                        Mark authorized
                      </Button>
                    )}
                    {o.state === "authorized" && (
                      <Button
                        size="sm"
                        onClick={() => transition(o.id, "consumed")}
                        className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-500 rounded-sm"
                      >
                        Mark consumed
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
